import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Chip, IconButton, Alert, Tooltip, Select,
  MenuItem, FormControl, InputLabel, useTheme, useMediaQuery
} from '@mui/material';
import { Add, Edit, Block } from '@mui/icons-material';
import { clientsAPI, usersAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import SlideTransition from '../../components/common/SlideTransition';
import DevBadge from '../../components/common/DevBadge';

export default function ClientsManagement() {
  const { user: me } = useAuth();
  const isSuperadmin = me?.role === 'superadmin';
  const [clients, setClients] = useState([]);
  const [teamLeads, setTeamLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', nit: '', contactEmail: '', contactPhone: '', teamLeadId: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState({ open: false, clientId: null });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const load = async () => {
    try {
      // The teamlead selector is only needed (and only resolvable) for a superadmin:
      // GET /api/users forces role=member for a teamlead caller, so it would never
      // return other team leads to populate this list.
      const [clientsRes, usersRes] = await Promise.all([
        clientsAPI.getAll(),
        isSuperadmin ? usersAPI.getAll({ role: 'teamlead' }) : Promise.resolve({ data: [] })
      ]);
      setClients(clientsRes.data);
      setTeamLeads(usersRes.data);
    } catch { } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setEditClient(null);
    setForm({ name: '', description: '', nit: '', contactEmail: '', contactPhone: '', teamLeadId: '' });
    setError('');
    setOpenDialog(true);
  };

  const openEdit = (client) => {
    setEditClient(client);
    const ownerId = client.teamLeadId?._id || client.teamLeadId || '';
    setForm({
      name: client.name, description: client.description || '',
      nit: client.nit || '', contactEmail: client.contactEmail || '',
      contactPhone: client.contactPhone || '', teamLeadId: ownerId
    });
    setError('');
    setOpenDialog(true);
  };

  const handleSave = async () => {
    setError('');
    setSaving(true);
    try {
      if (editClient) {
        await clientsAPI.update(editClient._id, form);
      } else {
        await clientsAPI.create(form);
      }
      setOpenDialog(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      <DevBadge id={isSuperadmin ? 'page:admin-clientes' : 'page:teamlead-clientes'} />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>Gestión de Clientes</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Nuevo Cliente</Button>
      </Box>

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Empresa</TableCell>
                <TableCell>NIT</TableCell>
                <TableCell>Contacto</TableCell>
                <TableCell>Líder de Equipo</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.map(client => {
                const tl = client.teamLeadId;
                const ownerId = tl?._id || tl;
                // Matches the backend rule (routes/clients.js): only the owning teamlead, or a
                // superadmin, may edit/deactivate a client — hide the actions otherwise.
                const canManage = isSuperadmin || ownerId === me?._id;
                return (
                  <TableRow key={client._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{client.name}</Typography>
                      {client.description && <Typography variant="caption" color="text.secondary">{client.description}</Typography>}
                    </TableCell>
                    <TableCell><Typography variant="body2">{client.nit || '—'}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{client.contactEmail || '—'}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {tl ? `${tl.name || ''} ${tl.lastName || ''}`.trim() || tl.email : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={client.status === 'active' ? 'Activo' : 'Inactivo'} size="small"
                        color={client.status === 'active' ? 'success' : 'error'} variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '2px' }}>
                        {canManage ? (
                          <>
                            <Tooltip title="Editar"><IconButton size="small" sx={{ borderRadius: '5px', bgcolor: 'rgba(0,0,0,0.06)', '&:hover': { bgcolor: 'rgba(0,0,0,0.13)' } }} onClick={() => openEdit(client)}><Edit fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Desactivar">
                              <IconButton size="small" color="error" sx={{ borderRadius: '5px', bgcolor: 'rgba(211,47,47,0.08)', '&:hover': { bgcolor: 'rgba(211,47,47,0.18)' } }} onClick={() => setConfirm({ open: true, clientId: client._id })}>
                                <Block fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {clients.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" py={3}>No hay clientes registrados</Typography>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile} TransitionComponent={SlideTransition}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          {editClient ? 'Editar Cliente' : 'Nuevo Cliente'}
          <DevBadge id="modal:cliente" inline />
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Nombre de la Empresa *" value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} fullWidth />
            <TextField label="Descripción" value={form.description} multiline rows={2}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))} fullWidth />
            <Box display="flex" gap={2}>
              <TextField label="NIT / ID Fiscal" value={form.nit}
                onChange={e => setForm(f => ({ ...f, nit: e.target.value }))} fullWidth />
              <TextField label="Teléfono de Contacto" value={form.contactPhone}
                onChange={e => setForm(f => ({ ...f, contactPhone: e.target.value }))} fullWidth />
            </Box>
            <TextField label="Email de Contacto" value={form.contactEmail} type="email"
              onChange={e => setForm(f => ({ ...f, contactEmail: e.target.value }))} fullWidth />
            {isSuperadmin ? (
              <FormControl fullWidth>
                <InputLabel>Líder de Equipo Asignado</InputLabel>
                <Select value={form.teamLeadId} label="Líder de Equipo Asignado"
                  onChange={e => setForm(f => ({ ...f, teamLeadId: e.target.value }))}>
                  {teamLeads.map(tl => (
                    <MenuItem key={tl._id} value={tl._id}>
                      {tl.name ? `${tl.name} ${tl.lastName || ''}` : tl.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : !editClient && (
              <Typography variant="caption" color="text.secondary">
                Se te asignará automáticamente como líder de este cliente. Otros líderes de equipo
                podrán ver y usar este cliente para sus propios proyectos.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.name}>
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirm.open} title="Desactivar Cliente"
        message="¿Desactivar este cliente? Los proyectos asociados quedarán inactivos."
        confirmLabel="Desactivar"
        onConfirm={async () => { await clientsAPI.delete(confirm.clientId); setConfirm({ open: false, clientId: null }); load(); }}
        onCancel={() => setConfirm({ open: false, clientId: null })}
      />
    </Box>
  );
}
